// Local, deterministic translation of the finite Estonian vocabulary returned
// by MNT. The API document is deliberately never mutated: callers translate
// these strings while rendering, which also makes cached responses follow live
// locale changes.

import type { IconStatus, OperationCode, StatusMessage } from '../api/checkVehicle';
import {
  normalizeVehicleKey,
  translateBodyType,
  translateColor,
  translateFuelType,
  translateTransmission,
  vehicleLabel,
  type VehicleTranslationRow as Row,
} from './soov';
import { countryCode } from './format';

export type MntValueKind =
  | 'category'
  | 'bodyType'
  | 'color'
  | 'fuel'
  | 'transmission'
  | 'drive'
  | 'fuelCombination'
  | 'location'
  | 'generic';

const r = (et: string, en: string, ru: string, lv: string, zh: string, th: string): Row => ({
  et,
  en,
  ru,
  lv,
  zh,
  th,
});

const CATEGORIES: Record<string, Row> = {
  soiduauto: r('Sõiduauto', 'Passenger car', 'Легковой автомобиль', 'Vieglais automobilis', '乘用车', 'รถยนต์นั่ง'),
  siduauto: r('Sõiduauto', 'Passenger car', 'Легковой автомобиль', 'Vieglais automobilis', '乘用车', 'รถยนต์นั่ง'),
  'maastikusuutlik soiduauto': r('Maastikusuutlik sõiduauto', 'Off-road passenger car', 'Легковой автомобиль повышенной проходимости', 'Paaugstinātas caurgājības vieglais automobilis', '越野乘用车', 'รถยนต์นั่งออฟโรด'),
  'maastikusuutlik siduauto': r('Maastikusuutlik sõiduauto', 'Off-road passenger car', 'Легковой автомобиль повышенной проходимости', 'Paaugstinātas caurgājības vieglais automobilis', '越野乘用车', 'รถยนต์นั่งออฟโรด'),
  veoauto: r('Veoauto', 'Truck', 'Грузовой автомобиль', 'Kravas automobilis', '货车', 'รถบรรทุก'),
  'maastikusuutlik veoauto': r('Maastikusuutlik veoauto', 'Off-road truck', 'Грузовой автомобиль повышенной проходимости', 'Paaugstinātas caurgājības kravas automobilis', '越野货车', 'รถบรรทุกออฟโรด'),
  mootorratas: r('Mootorratas', 'Motorcycle', 'Мотоцикл', 'Motocikls', '摩托车', 'รถจักรยานยนต์'),
  buss: r('Buss', 'Bus', 'Автобус', 'Autobuss', '客车', 'รถโดยสาร'),
};

const DRIVES: Record<string, Row> = {
  esivedu: r('Esivedu', 'Front-wheel drive', 'Передний привод', 'Priekšpiedziņa', '前轮驱动', 'ขับเคลื่อนล้อหน้า'),
  nelikvedu: r('Nelikvedu', 'All-wheel drive', 'Полный привод', 'Pilnpiedziņa', '全轮驱动', 'ขับเคลื่อนสี่ล้อ'),
  tagavedu: r('Tagavedu', 'Rear-wheel drive', 'Задний привод', 'Aizmugures piedziņa', '后轮驱动', 'ขับเคลื่อนล้อหลัง'),
};

const FUEL_COMBINATIONS: Record<string, Row> = {
  'uhekutuseline (mono fuel)': r('Ühekütuseline (mono fuel)', 'Mono-fuel vehicle', 'Монотопливный автомобиль', 'Viena veida degvielas transportlīdzeklis', '单燃料车辆', 'รถเชื้อเพลิงชนิดเดียว'),
  'hektuseline (mono fuel)': r('Ühekütuseline (mono fuel)', 'Mono-fuel vehicle', 'Монотопливный автомобиль', 'Viena veida degvielas transportlīdzeklis', '单燃料车辆', 'รถเชื้อเพลิงชนิดเดียว'),
  'kahekutuseline (bi fuel)': r('Kahekütuseline (bi fuel)', 'Bi-fuel vehicle', 'Двухтопливный автомобиль', 'Divu veidu degvielas transportlīdzeklis', '双燃料车辆', 'รถเชื้อเพลิงสองชนิด'),
  'kahektuseline (bi fuel)': r('Kahekütuseline (bi fuel)', 'Bi-fuel vehicle', 'Двухтопливный автомобиль', 'Divu veidu degvielas transportlīdzeklis', '双燃料车辆', 'รถเชื้อเพลิงสองชนิด'),
  hubriidsoiduk: r('Hübriidsõiduk', 'Hybrid vehicle', 'Гибридный автомобиль', 'Hibrīdtransportlīdzeklis', '混合动力车辆', 'รถไฮบริด'),
  hbriidsiduk: r('Hübriidsõiduk', 'Hybrid vehicle', 'Гибридный автомобиль', 'Hibrīdtransportlīdzeklis', '混合动力车辆', 'รถไฮบริด'),
  elektrisoiduk: r('Elektrisõiduk', 'Electric vehicle', 'Электромобиль', 'Elektrotransportlīdzeklis', '电动车', 'รถยนต์ไฟฟ้า'),
  elektrisiduk: r('Elektrisõiduk', 'Electric vehicle', 'Электромобиль', 'Elektrotransportlīdzeklis', '电动车', 'รถยนต์ไฟฟ้า'),
};

function tableValue(table: Record<string, Row>, raw: string | null | undefined, locale: string): string | null {
  const trimmed = raw?.trim();
  if (!trimmed) return null;
  const row = table[normalizeVehicleKey(trimmed)];
  return row ? vehicleLabel(row, locale) : trimmed;
}

export function translateCategory(raw: string | null | undefined, locale: string): string | null {
  return tableValue(CATEGORIES, raw, locale);
}

export function translateDriveType(raw: string | null | undefined, locale: string): string | null {
  return tableValue(DRIVES, raw, locale);
}

export function translateFuelCombination(raw: string | null | undefined, locale: string): string | null {
  return tableValue(FUEL_COMBINATIONS, raw, locale);
}

// The two location fields contain many combinations of the same small set of
// components and directions. Exact common phrases get polished translations;
// the token lexicon handles every combination observed in the supplied MNT
// inventory, including legacy strings with missing Estonian diacritics.
const LOCATION_EXACT: Record<string, Row> = {
  'paremal esipaneelil': r('Paremal esipaneelil', 'On the right front panel', 'На передней панели справа', 'Uz priekšējā paneļa labajā pusē', '右侧前围板上', 'บนแผงด้านหน้าฝั่งขวา'),
  'mootoriruumis paremal': r('Mootoriruumis paremal', 'In the engine bay on the right', 'В моторном отсеке справа', 'Motora nodalījumā labajā pusē', '发动机舱右侧', 'ในห้องเครื่องฝั่งขวา'),
  'mootoriruumis': r('Mootoriruumis', 'In the engine bay', 'В моторном отсеке', 'Motora nodalījumā', '发动机舱内', 'ในห้องเครื่อง'),
  'b-piilaril': r('B-piilaril', 'On the B-pillar', 'На центральной стойке', 'Uz B statņa', 'B柱上', 'บนเสา B'),
  'pagasiruumis': r('Pagasiruumis', 'In the luggage compartment', 'В багажном отделении', 'Bagāžas nodalījumā', '行李舱内', 'ในห้องเก็บสัมภาระ'),
  'pakiruumis': r('Pakiruumis', 'In the luggage compartment', 'В багажном отделении', 'Bagāžas nodalījumā', '行李舱内', 'ในห้องเก็บสัมภาระ'),
  puudub: r('Puudub', 'Not present', 'Отсутствует', 'Nav', '无', 'ไม่มี'),
};

const LOCATION_REPAIRS: Array<[RegExp, string]> = [
  [/\bprandal\b/g, 'põrandal'],
  [/\bkrvalistuja\b/g, 'kõrvalistuja'],
  [/\bkrvalistme\b/g, 'kõrvalistme'],
  [/\bkrval\b/g, 'kõrval'],
  [/\bkljeplekil\b/g, 'küljeplekil'],
  [/\btagakljel\b/g, 'tagaküljel'],
  [/\bsisekljel\b/g, 'siseküljel'],
  [/\bkljel\b/g, 'küljel'],
  [/\blvel\b/g, 'lävel'],
  [/\bpsttorul\b/g, 'püsttorul'],
  [/\bamordipstakul\b/g, 'amordipüstakul'],
  [/\bketoe\b/g, 'käetoe'],
  [/\blal\b/g, 'ülal'],
  [/\bvi\b/g, 'või'],
  [/\bmootriruumis\b/g, 'mootoriruumis'],
  [/\bparemall\b/g, 'paremal'],
  [/\bparempoolel\b/g, 'parempoolsel'],
  [/\bparempoorlel\b/g, 'parempoolsel'],
];

const LOCATION_WORDS: Record<string, Row> = {
  mootoriruumis: r('mootoriruumis', 'engine bay', 'моторный отсек', 'motora nodalījums', '发动机舱', 'ห้องเครื่อง'),
  mootoriruumi: r('mootoriruumi', 'engine bay', 'моторного отсека', 'motora nodalījuma', '发动机舱', 'ห้องเครื่อง'),
  mootori: r('mootori', 'engine', 'двигателя', 'dzinēja', '发动机', 'เครื่องยนต์'),
  ruumis: r('ruumis', 'compartment', 'отсек', 'nodalījums', '舱内', 'ห้อง'),
  paremal: r('paremal', 'right', 'справа', 'labajā pusē', '右侧', 'ด้านขวา'),
  parem: r('parem', 'right', 'правый', 'labais', '右侧', 'ขวา'),
  parema: r('parema', 'right', 'правого', 'labās', '右侧', 'ขวา'),
  parempoolse: r('parempoolse', 'right-side', 'правого', 'labās puses', '右侧', 'ฝั่งขวา'),
  parempoolsel: r('parempoolsel', 'right-side', 'справа', 'labajā pusē', '右侧', 'ฝั่งขวา'),
  vasakul: r('vasakul', 'left', 'слева', 'kreisajā pusē', '左侧', 'ด้านซ้าย'),
  vasaku: r('vasaku', 'left', 'левого', 'kreisās', '左侧', 'ซ้าย'),
  vasakpoolse: r('vasakpoolse', 'left-side', 'левого', 'kreisās puses', '左侧', 'ฝั่งซ้าย'),
  vasakpoolsel: r('vasakpoolsel', 'left-side', 'слева', 'kreisajā pusē', '左侧', 'ฝั่งซ้าย'),
  ees: r('ees', 'front', 'спереди', 'priekšā', '前方', 'ด้านหน้า'),
  eesmisel: r('eesmisel', 'front', 'переднем', 'priekšējā', '前部', 'ด้านหน้า'),
  taga: r('taga', 'behind', 'сзади', 'aizmugurē', '后方', 'ด้านหลัง'),
  tagapool: r('tagapool', 'behind', 'сзади', 'aizmugurē', '后方', 'ด้านหลัง'),
  tagumise: r('tagumise', 'rear', 'заднего', 'aizmugurējā', '后部', 'ด้านหลัง'),
  tagumisel: r('tagumisel', 'rear', 'заднем', 'aizmugurējā', '后部', 'ด้านหลัง'),
  keskel: r('keskel', 'center', 'по центру', 'centrā', '中央', 'กึ่งกลาง'),
  keskosas: r('keskosas', 'central area', 'в центральной части', 'centrālajā daļā', '中央区域', 'บริเวณกึ่งกลาง'),
  ülal: r('ülal', 'upper', 'сверху', 'augšā', '上部', 'ด้านบน'),
  all: r('all', 'under', 'под', 'zem', '下方', 'ใต้'),
  allosas: r('allosas', 'lower part', 'в нижней части', 'apakšējā daļā', '下部', 'ส่วนล่าง'),
  alaosas: r('alaosas', 'lower part', 'в нижней части', 'apakšējā daļā', '下部', 'ส่วนล่าง'),
  kohal: r('kohal', 'above', 'над', 'virs', '上方', 'เหนือ'),
  kõrval: r('kõrval', 'beside', 'рядом', 'blakus', '旁边', 'ข้าง'),
  ja: r('ja', 'and', 'и', 'un', '和', 'และ'),
  või: r('või', 'or', 'или', 'vai', '或', 'หรือ'),
  esiistme: r('esiistme', 'front seat', 'переднего сиденья', 'priekšējā sēdekļa', '前排座椅', 'เบาะหน้า'),
  istme: r('istme', 'seat', 'сиденья', 'sēdekļa', '座椅', 'เบาะนั่ง'),
  juhiistme: r('juhiistme', 'driver seat', 'сиденья водителя', 'vadītāja sēdekļa', '驾驶员座椅', 'เบาะคนขับ'),
  kõrvalistme: r('kõrvalistme', 'passenger seat', 'пассажирского сиденья', 'pasažiera sēdekļa', '副驾驶座椅', 'เบาะผู้โดยสารหน้า'),
  kõrvalistuja: r('kõrvalistuja', 'passenger', 'пассажира', 'pasažiera', '乘客', 'ผู้โดยสาร'),
  tagaistme: r('tagaistme', 'rear seat', 'заднего сиденья', 'aizmugurējā sēdekļa', '后排座椅', 'เบาะหลัง'),
  seljatoe: r('seljatoe', 'seat back', 'спинки сиденья', 'sēdekļa atzveltnes', '座椅靠背', 'พนักพิงเบาะ'),
  käetoe: r('käetoe', 'armrest', 'подлокотника', 'roku balsta', '扶手', 'ที่วางแขน'),
  põrandal: r('põrandal', 'floor', 'на полу', 'uz grīdas', '地板上', 'บนพื้น'),
  salongis: r('salongis', 'passenger compartment', 'в салоне', 'salonā', '乘员舱内', 'ในห้องโดยสาร'),
  salongi: r('salongi', 'passenger compartment', 'салона', 'salona', '乘员舱', 'ห้องโดยสาร'),
  juhiruumis: r('juhiruumis', 'driver compartment', 'в кабине водителя', 'vadītāja kabīnē', '驾驶舱内', 'ในห้องคนขับ'),
  juhiruumi: r('juhiruumi', 'driver compartment', 'кабины водителя', 'vadītāja kabīnes', '驾驶舱', 'ห้องคนขับ'),
  esiklaasi: r('esiklaasi', 'windscreen', 'ветрового стекла', 'vējstikla', '前挡风玻璃', 'กระจกหน้า'),
  tuuleklaasi: r('tuuleklaasi', 'windscreen', 'ветрового стекла', 'vējstikla', '前挡风玻璃', 'กระจกหน้า'),
  esiakna: r('esiakna', 'windscreen', 'ветрового стекла', 'vējstikla', '前挡风玻璃', 'กระจกหน้า'),
  alumise: r('alumise', 'lower', 'нижнего', 'apakšējā', '下部', 'ล่าง'),
  nurga: r('nurga', 'corner', 'угла', 'stūra', '角', 'มุม'),
  alaservas: r('alaservas', 'lower edge', 'у нижнего края', 'apakšmalā', '下边缘', 'ขอบล่าง'),
  esipaneelil: r('esipaneelil', 'front panel', 'на передней панели', 'uz priekšējā paneļa', '前围板', 'แผงด้านหน้า'),
  esipaneeli: r('esipaneeli', 'front panel', 'передней панели', 'priekšējā paneļa', '前围板', 'แผงด้านหน้า'),
  tagapaneelil: r('tagapaneelil', 'rear panel', 'на задней панели', 'uz aizmugurējā paneļa', '后围板', 'แผงด้านหลัง'),
  tagapaneel: r('tagapaneel', 'rear panel', 'задняя панель', 'aizmugurējais panelis', '后围板', 'แผงด้านหลัง'),
  tagaseinal: r('tagaseinal', 'rear wall', 'на задней стенке', 'uz aizmugurējās sienas', '后壁', 'ผนังด้านหลัง'),
  tagaseina: r('tagaseina', 'rear wall', 'задней стенки', 'aizmugurējās sienas', '后壁', 'ผนังด้านหลัง'),
  seinal: r('seinal', 'wall', 'на стенке', 'uz sienas', '壁面', 'ผนัง'),
  rattakoopal: r('rattakoopal', 'wheel arch', 'на колёсной арке', 'uz riteņa arkas', '轮拱', 'ซุ้มล้อ'),
  rattakoopa: r('rattakoopa', 'wheel arch', 'колёсной арки', 'riteņa arkas', '轮拱', 'ซุ้มล้อ'),
  esirattakoopal: r('esirattakoopal', 'front wheel arch', 'на передней колёсной арке', 'uz priekšējā riteņa arkas', '前轮拱', 'ซุ้มล้อหน้า'),
  koopas: r('koopas', 'wheel well', 'в колёсной нише', 'riteņa arkā', '轮罩内', 'ในซุ้มล้อ'),
  koopal: r('koopal', 'wheel arch', 'на колёсной арке', 'uz riteņa arkas', '轮拱', 'ซุ้มล้อ'),
  esiratta: r('esiratta', 'front wheel', 'переднего колеса', 'priekšējā riteņa', '前轮', 'ล้อหน้า'),
  tagavararatta: r('tagavararatta', 'spare wheel', 'запасного колеса', 'rezerves riteņa', '备胎', 'ล้ออะไหล่'),
  tiiva: r('tiiva', 'fender', 'крыла', 'spārna', '翼子板', 'บังโคลน'),
  tiival: r('tiival', 'fender', 'на крыле', 'uz spārna', '翼子板', 'บังโคลน'),
  esitiiva: r('esitiiva', 'front fender', 'переднего крыла', 'priekšējā spārna', '前翼子板', 'บังโคลนหน้า'),
  esitiival: r('esitiival', 'front fender', 'на переднем крыле', 'uz priekšējā spārna', '前翼子板', 'บังโคลนหน้า'),
  siseküljel: r('siseküljel', 'inner side', 'с внутренней стороны', 'iekšpusē', '内侧', 'ด้านใน'),
  küljeplekil: r('küljeplekil', 'side panel', 'на боковой панели', 'uz sānu paneļa', '侧围板', 'แผงด้านข้าง'),
  küljel: r('küljel', 'side', 'сбоку', 'sānā', '侧面', 'ด้านข้าง'),
  poritiival: r('poritiival', 'fender', 'на крыле', 'uz spārna', '翼子板', 'บังโคลน'),
  piilaril: r('piilaril', 'pillar', 'на стойке', 'uz statņa', '柱上', 'บนเสา'),
  'a-piilaril': r('A-piilaril', 'A-pillar', 'на передней стойке', 'uz A statņa', 'A柱', 'บนเสา A'),
  'b-piilaril': r('B-piilaril', 'B-pillar', 'на центральной стойке', 'uz B statņa', 'B柱', 'บนเสา B'),
  'c-piilaril': r('C-piilaril', 'C-pillar', 'на задней стойке', 'uz C statņa', 'C柱', 'บนเสา C'),
  'd-piilaril': r('D-piilaril', 'D-pillar', 'на стойке D', 'uz D statņa', 'D柱', 'บนเสา D'),
  'b-piilari': r('B-piilari', 'B-pillar', 'центральной стойки', 'B statņa', 'B柱', 'เสา B'),
  keskpostil: r('keskpostil', 'center pillar', 'на центральной стойке', 'uz vidējā statņa', '中柱', 'บนเสากลาง'),
  uksepostil: r('uksepostil', 'door pillar', 'на стойке двери', 'uz durvju statņa', '门柱', 'เสาประตู'),
  tagapostil: r('tagapostil', 'rear door pillar', 'на задней стойке двери', 'uz aizmugurējā durvju statņa', '后门柱', 'เสาประตูด้านหลัง'),
  esipostil: r('esipostil', 'front door pillar', 'на передней стойке двери', 'uz priekšējā durvju statņa', '前门柱', 'เสาประตูด้านหน้า'),
  posti: r('posti', 'pillar', 'стойки', 'statņa', '柱', 'เสา'),
  postil: r('postil', 'pillar', 'на стойке', 'uz statņa', '柱上', 'บนเสา'),
  esiukse: r('esiukse', 'front door', 'передней двери', 'priekšējo durvju', '前门', 'ประตูหน้า'),
  tagaukse: r('tagaukse', 'rear door', 'задней двери', 'aizmugurējo durvju', '后门', 'ประตูหลัง'),
  juhiukse: r('juhiukse', 'driver door', 'двери водителя', 'vadītāja durvju', '驾驶员车门', 'ประตูคนขับ'),
  juhiuksel: r('juhiuksel', 'driver door', 'на двери водителя', 'uz vadītāja durvīm', '驾驶员车门', 'บนประตูคนขับ'),
  esiuksel: r('esiuksel', 'front door', 'на передней двери', 'uz priekšējām durvīm', '前门', 'บนประตูหน้า'),
  ukse: r('ukse', 'door', 'двери', 'durvju', '车门', 'ประตู'),
  lävel: r('lävel', 'door sill', 'на пороге', 'uz sliekšņa', '门槛', 'ธรณีประตู'),
  avas: r('avas', 'opening', 'в проёме', 'ailē', '开口处', 'ช่องเปิด'),
  piidal: r('piidal', 'door jamb', 'на дверном косяке', 'uz durvju aplodas', '门框', 'วงกบประตู'),
  vedru: r('vedru', 'spring', 'пружины', 'atsperes', '弹簧', 'สปริง'),
  esivedru: r('esivedru', 'front spring', 'передней пружины', 'priekšējās atsperes', '前弹簧', 'สปริงหน้า'),
  toel: r('toel', 'support', 'на опоре', 'uz balsta', '支座', 'ฐานรอง'),
  vedrukanduril: r('vedrukanduril', 'spring support', 'на опоре пружины', 'uz atsperes balsta', '弹簧支座', 'ฐานรองสปริง'),
  amortisaatori: r('amortisaatori', 'shock absorber', 'амортизатора', 'amortizatora', '减震器', 'โช้คอัพ'),
  amordi: r('amordi', 'shock absorber', 'амортизатора', 'amortizatora', '减震器', 'โช้คอัพ'),
  esiamordi: r('esiamordi', 'front shock absorber', 'переднего амортизатора', 'priekšējā amortizatora', '前减震器', 'โช้คอัพหน้า'),
  amordipüstakul: r('amordipüstakul', 'shock-absorber tower', 'на стойке амортизатора', 'uz amortizatora statņa', '减震器塔顶', 'หอคอยโช้คอัพ'),
  amordikinnitusel: r('amordikinnitusel', 'shock-absorber mount', 'на креплении амортизатора', 'uz amortizatora stiprinājuma', '减震器固定座', 'จุดยึดโช้คอัพ'),
  tugevdusel: r('tugevdusel', 'reinforcement', 'на усилителе', 'uz pastiprinājuma', '加强件', 'แผ่นเสริม'),
  raam: r('raam', 'frame', 'рама', 'rāmis', '车架', 'โครงรถ'),
  raami: r('raami', 'frame', 'рамы', 'rāmja', '车架', 'โครงรถ'),
  raamil: r('raamil', 'frame', 'на раме', 'uz rāmja', '车架', 'บนโครงรถ'),
  püsttorul: r('püsttorul', 'vertical frame tube', 'на вертикальной трубе рамы', 'uz vertikālās rāmja caurules', '车架竖管', 'ท่อแนวตั้งของโครงรถ'),
  pikitalal: r('pikitalal', 'longitudinal member', 'на лонжероне', 'uz garensijas', '纵梁', 'คานตามยาว'),
  piktalal: r('piktalal', 'longitudinal member', 'на лонжероне', 'uz garensijas', '纵梁', 'คานตามยาว'),
  risttalal: r('risttalal', 'crossmember', 'на поперечине', 'uz šķērssijas', '横梁', 'คานขวาง'),
  risttoel: r('risttoel', 'cross support', 'на поперечной опоре', 'uz šķērsbalsta', '横向支座', 'ฐานรองขวาง'),
  tala: r('tala', 'beam', 'балка', 'sija', '梁', 'คาน'),
  lanseronil: r('lanseronil', 'longitudinal member', 'на лонжероне', 'uz garensijas', '纵梁', 'คานตามยาว'),
  tunnelil: r('tunnelil', 'center tunnel', 'на центральном тоннеле', 'uz centrālā tuneļa', '中央通道', 'อุโมงค์กลาง'),
  pagasiruumis: r('pagasiruumis', 'luggage compartment', 'багажное отделение', 'bagāžas nodalījums', '行李舱', 'ห้องเก็บสัมภาระ'),
  pakiruumis: r('pakiruumis', 'luggage compartment', 'багажное отделение', 'bagāžas nodalījums', '行李舱', 'ห้องเก็บสัมภาระ'),
  kapoti: r('kapoti', 'bonnet', 'капотом', 'motora pārsega', '发动机盖', 'ฝากระโปรง'),
  plastkatte: r('plastkatte', 'plastic cover', 'пластиковой крышкой', 'plastmasas pārsega', '塑料盖板', 'ฝาครอบพลาสติก'),
  klapi: r('klapi', 'flap', 'крышки', 'vāka', '盖板', 'ฝาปิด'),
  ava: r('ava', 'opening', 'проёма', 'ailes', '开口', 'ช่องเปิด'),
  alusel: r('alusel', 'base', 'на основании', 'uz pamatnes', '底座', 'ฐาน'),
  aluskastil: r('aluskastil', 'seat base box', 'на коробе основания сиденья', 'uz sēdekļa pamatnes kastes', '座椅底座箱', 'กล่องฐานเบาะ'),
  plekil: r('plekil', 'panel', 'на панели', 'uz paneļa', '板件', 'แผง'),
  paneelil: r('paneelil', 'panel', 'на панели', 'uz paneļa', '板件', 'แผง'),
  esilaterna: r('esilaterna', 'headlamp', 'передней фары', 'priekšējā luktura', '前照灯', 'ไฟหน้า'),
  teljest: r('teljest', 'from axle', 'от оси', 'no ass', '从车轴', 'จากเพลา'),
  padja: r('padja', 'mount', 'опоры', 'balsta', '支座', 'แท่นรอง'),
  juures: r('juures', 'near', 'рядом', 'pie', '附近', 'ใกล้'),
  puudub: r('puudub', 'not present', 'отсутствует', 'nav', '无', 'ไม่มี'),
};

function repairLocation(raw: string): string {
  let value = raw.trim().toLowerCase().replace(/\s+/g, ' ');
  for (const [pattern, replacement] of LOCATION_REPAIRS) value = value.replace(pattern, replacement);
  return value;
}

function sentenceCase(value: string): string {
  return value.length > 0 ? value.charAt(0).toUpperCase() + value.slice(1) : value;
}

export function translateLocation(raw: string | null | undefined, locale: string): string | null {
  const trimmed = raw?.trim();
  if (!trimmed) return null;
  if (/^[?,\s\dxyzr.-]+$/i.test(trimmed)) return trimmed;
  const repaired = repairLocation(trimmed);
  const exact = LOCATION_EXACT[repaired];
  if (exact) return vehicleLabel(exact, locale);
  if (locale === 'et') return sentenceCase(repaired);

  let translatedAny = false;
  const translated = repaired.replace(/[\p{L}]+(?:-[\p{L}]+)*/gu, (word) => {
    const row = LOCATION_WORDS[word];
    if (!row) return word;
    translatedAny = true;
    return vehicleLabel(row, locale);
  });
  return translatedAny ? sentenceCase(translated.replace(/\s+/g, ' ')) : trimmed;
}

export function translateMntValue(
  kind: MntValueKind,
  raw: string | null | undefined,
  locale: string,
): string | null {
  if (kind === 'category') return translateCategory(raw, locale);
  if (kind === 'bodyType') return translateBodyType(raw, locale);
  if (kind === 'color') return translateColor(raw, locale);
  if (kind === 'fuel') return translateFuelType(raw, locale);
  if (kind === 'transmission') return translateTransmission(raw, locale);
  if (kind === 'drive') return translateDriveType(raw, locale);
  if (kind === 'fuelCombination') return translateFuelCombination(raw, locale);
  if (kind === 'location') return translateLocation(raw, locale);
  return translateGenericValue(raw, locale);
}

export interface MntFieldTranslation {
  labelKey: string | null;
  kind: MntValueKind;
}

const FIELD_LABELS: Record<string, MntFieldTranslation> = {
  kategooria: { labelKey: 'details.labels.category', kind: 'category' },
  'kere nimetus': { labelKey: 'details.labels.bodyType', kind: 'bodyType' },
  'kere varvus': { labelKey: 'details.labels.color', kind: 'color' },
  'kere vrvus': { labelKey: 'details.labels.color', kind: 'color' },
  kutus: { labelKey: 'details.labels.fuel', kind: 'fuel' },
  ktus: { labelKey: 'details.labels.fuel', kind: 'fuel' },
  kaigukast: { labelKey: 'details.labels.transmission', kind: 'transmission' },
  kigukast: { labelKey: 'details.labels.transmission', kind: 'transmission' },
  veoskeem: { labelKey: 'details.labels.drive', kind: 'drive' },
  'kutuse kombinatsioon': { labelKey: 'details.labels.fuelCombination', kind: 'fuelCombination' },
  'ktuse kombinatsioon': { labelKey: 'details.labels.fuelCombination', kind: 'fuelCombination' },
  'vin-koodi asukoht': { labelKey: 'details.labels.vinLocation', kind: 'location' },
  'valmistaja andmesildi asukoht': { labelKey: 'details.labels.manufacturerPlateLocation', kind: 'location' },
  'esmane registreerimine': { labelKey: 'details.labels.firstRegistration', kind: 'generic' },
  mootor: { labelKey: 'details.labels.engine', kind: 'generic' },
  teljed: { labelKey: 'details.labels.axleCount', kind: 'generic' },
  rehvid: { labelKey: 'details.labels.tires', kind: 'generic' },
  'haagise lisainfo': { labelKey: 'details.labels.trailerInfo', kind: 'generic' },
};

export function mntFieldTranslation(label: string): MntFieldTranslation {
  const key = normalizeVehicleKey(label.replace(/:\s*$/, ''));
  return FIELD_LABELS[key] ?? { labelKey: null, kind: 'generic' };
}

const GENERIC_VALUES: Record<string, Row> = {
  puudub: r('Puudub', 'None', 'Нет', 'Nav', '无', 'ไม่มี'),
  jah: r('Jah', 'Yes', 'Да', 'Jā', '是', 'ใช่'),
  ei: r('Ei', 'No', 'Нет', 'Nē', '否', 'ไม่'),
  tasutud: r('Tasutud', 'Paid', 'Оплачено', 'Samaksāts', '已支付', 'ชำระแล้ว'),
};

export function translateGenericValue(raw: string | null | undefined, locale: string): string | null {
  return tableValue(GENERIC_VALUES, raw, locale);
}

const REGISTRATION_CONTEXTS: Record<string, Row> = {
  new_in_estonia: r('Registreeritud Eestis uuena', 'Registered new in Estonia', 'Впервые зарегистрирован новым в Эстонии', 'Reģistrēts jauns Igaunijā', '在爱沙尼亚以新车注册', 'จดทะเบียนเป็นรถใหม่ในเอสโตเนีย'),
  used_in_estonia: r('Registreeritud Eestis kasutatuna', 'Registered used in Estonia', 'Зарегистрирован подержанным в Эстонии', 'Reģistrēts lietots Igaunijā', '在爱沙尼亚以二手车注册', 'จดทะเบียนเป็นรถใช้แล้วในเอสโตเนีย'),
  in_estonia: r('Registreeritud Eestis', 'Registered in Estonia', 'Зарегистрирован в Эстонии', 'Reģistrēts Igaunijā', '在爱沙尼亚注册', 'จดทะเบียนในเอสโตเนีย'),
};

export function translateRegistrationContext(
  context: string | null | undefined,
  raw: string | null | undefined,
  locale: string,
): string | null {
  const row = context ? REGISTRATION_CONTEXTS[context] : undefined;
  return row ? vehicleLabel(row, locale) : translateGenericValue(raw, locale);
}

export function translateCountryName(raw: string | null | undefined, locale: string): string | null {
  const trimmed = raw?.trim();
  if (!trimmed) return null;
  const code = countryCode(trimmed);
  if (!code) return trimmed;
  try {
    return new Intl.DisplayNames([locale], { type: 'region' }).of(code) ?? trimmed;
  } catch {
    return trimmed;
  }
}

const REGISTRATION_FEES: Record<string, Row> = {
  paid: r('Tasutud', 'Paid', 'Оплачено', 'Samaksāts', '已支付', 'ชำระแล้ว'),
  payable_on_next_transfer: r('Tasuda järgmisel omanikuvahetusel', 'Payable on next transfer', 'Оплачивается при следующей смене владельца', 'Jāmaksā nākamajā īpašnieka maiņā', '下次过户时支付', 'ชำระเมื่อโอนกรรมสิทธิ์ครั้งถัดไป'),
  unpaid: r('Tasumata', 'Unpaid', 'Не оплачено', 'Nav samaksāts', '未支付', 'ยังไม่ชำระ'),
  unknown: r('Teadmata', 'Unknown', 'Неизвестно', 'Nav zināms', '未知', 'ไม่ทราบ'),
};

export function translateRegistrationFee(
  status: string | null | undefined,
  raw: string | null | undefined,
  locale: string,
): string | null {
  const row = status ? REGISTRATION_FEES[status] : undefined;
  return row ? vehicleLabel(row, locale) : translateGenericValue(raw, locale);
}

const STATUS_MESSAGES: Partial<Record<StatusMessage['kind'], Row>> = {
  register_pledge: r('Sõidukil on registripant', 'Vehicle has a register pledge', 'На автомобиле есть регистровый залог', 'Transportlīdzeklim ir reģistra ķīla', '车辆存在登记质押', 'รถมีภาระจำนำในทะเบียน'),
  inspection_valid: r('Tehnoülevaatus on kehtiv', 'Technical inspection is valid', 'Техосмотр действителен', 'Tehniskā apskate ir derīga', '技术检验有效', 'การตรวจสภาพยังใช้ได้'),
  inspection_expired: r('Tehnoülevaatus on aegunud', 'Technical inspection has expired', 'Срок техосмотра истёк', 'Tehniskās apskates termiņš ir beidzies', '技术检验已过期', 'การตรวจสภาพหมดอายุ'),
  insurance_active: r('Liikluskindlustus on kehtiv', 'Motor insurance is active', 'Страхование ответственности действует', 'OCTA apdrošināšana ir spēkā', '机动车保险有效', 'ประกันภัยรถยนต์ยังมีผล'),
  insurance_missing: r('Kehtiv liikluskindlustus puudub', 'No active motor insurance', 'Нет действующей страховки', 'Nav derīgas OCTA apdrošināšanas', '无有效机动车保险', 'ไม่มีประกันภัยรถยนต์ที่มีผล'),
  temporarily_deleted: r('Sõiduk on ajutiselt registrist kustutatud', 'Vehicle is temporarily deregistered', 'Автомобиль временно снят с учёта', 'Transportlīdzeklis uz laiku noņemts no uzskaites', '车辆已暂时注销登记', 'รถถูกระงับทะเบียนชั่วคราว'),
  deleted: r('Sõiduk on registrist kustutatud', 'Vehicle is deregistered', 'Автомобиль снят с учёта', 'Transportlīdzeklis noņemts no uzskaites', '车辆已注销登记', 'รถถูกยกเลิกทะเบียน'),
  theft: r('Sõiduk on kuulutatud varastatuks', 'Vehicle is reported stolen', 'Автомобиль числится угнанным', 'Transportlīdzeklis izsludināts meklēšanā kā zagts', '车辆被报失窃', 'รถถูกแจ้งว่าถูกขโมย'),
};

export function translateStatusMessage(message: StatusMessage, locale: string): string | null {
  const row = STATUS_MESSAGES[message.kind];
  return row ? vehicleLabel(row, locale) : message.text;
}

const OPERATIONS: Partial<Record<OperationCode, Row>> = {
  owner_change_with_fee: r('Omanikuvahetus koos riigilõivuga', 'Owner changed (fee paid)', 'Смена владельца (с пошлиной)', 'Īpašnieka maiņa (ar nodevu)', '车主变更（已缴费）', 'เปลี่ยนเจ้าของ (ชำระค่าธรรมเนียม)'),
  owner_change: r('Omanikuvahetus', 'Owner changed', 'Смена владельца', 'Īpašnieka maiņa', '车主变更', 'เปลี่ยนเจ้าของ'),
  registration_certificate_not_returned: r('Registreerimistunnistus tagastamata', 'Registration certificate not returned', 'Свидетельство о регистрации не возвращено', 'Reģistrācijas apliecība nav atdota', '登记证书未交回', 'ไม่ได้คืนใบทะเบียน'),
  registration_certificate_issued: r('Registreerimistunnistus väljastatud', 'Registration certificate issued', 'Выдано свидетельство о регистрации', 'Izsniegta reģistrācijas apliecība', '已签发登记证书', 'ออกใบทะเบียนแล้ว'),
  ordered_plate_issued: r('Tellitud registreerimismärk väljastatud', 'Ordered plate issued', 'Выдан заказной номерной знак', 'Izsniegta pasūtītā numura zīme', '已签发定制号牌', 'ออกป้ายทะเบียนสั่งทำแล้ว'),
  plate_reissued: r('Registreerimismärk uuesti väljastatud', 'Plate reissued', 'Номерной знак выдан повторно', 'Numura zīme izsniegta atkārtoti', '号牌已补发', 'ออกป้ายทะเบียนใหม่แล้ว'),
  plate_action: r('Registreerimismärgi toiming', 'Plate operation', 'Операция с номерным знаком', 'Darbība ar numura zīmi', '号牌操作', 'ดำเนินการเกี่ยวกับป้ายทะเบียน'),
  plate_lost_or_stolen: r('Registreerimismärk kadunud või varastatud', 'Plate lost or stolen', 'Номерной знак утерян или украден', 'Numura zīme nozaudēta vai nozagta', '号牌丢失或被盗', 'ป้ายทะเบียนสูญหายหรือถูกขโมย'),
  plate_returned: r('Registreerimismärk tagastatud', 'Plate returned', 'Номерной знак возвращён', 'Numura zīme atdota', '号牌已交回', 'คืนป้ายทะเบียนแล้ว'),
  plate_issued: r('Registreerimismärk väljastatud', 'Plate issued', 'Номерной знак выдан', 'Numura zīme izsniegta', '已签发号牌', 'ออกป้ายทะเบียนแล้ว'),
  registered_in_estonia: r('Registreeritud Eestis', 'Registered in Estonia', 'Зарегистрирован в Эстонии', 'Reģistrēts Igaunijā', '在爱沙尼亚注册', 'จดทะเบียนในเอสโตเนีย'),
  pre_registration_check: r('Registreerimiseelne kontroll', 'Pre-registration inspection', 'Предрегистрационная проверка', 'Pirmsreģistrācijas pārbaude', '注册前检验', 'ตรวจสอบก่อนจดทะเบียน'),
  temporary_deregistration_ended: r('Ajutine registrist kustutamine lõpetatud', 'Temporary deregistration ended', 'Временное снятие с учёта прекращено', 'Pagaidu noņemšana no uzskaites izbeigta', '临时注销已结束', 'สิ้นสุดการระงับทะเบียนชั่วคราว'),
  temporary_deregistration: r('Ajutiselt registrist kustutatud', 'Temporarily deregistered', 'Временно снят с учёта', 'Uz laiku noņemts no uzskaites', '暂时注销登记', 'ระงับทะเบียนชั่วคราว'),
  permanent_deregistration: r('Lõplikult registrist kustutatud', 'Permanently deregistered', 'Окончательно снят с учёта', 'Pilnībā noņemts no uzskaites', '永久注销登记', 'ยกเลิกทะเบียนถาวร'),
  deregistration: r('Registrist kustutatud', 'Deregistered', 'Снят с учёта', 'Noņemts no uzskaites', '注销登记', 'ยกเลิกทะเบียน'),
  technical_inspection: r('Tehnoülevaatus', 'Technical inspection', 'Техосмотр', 'Tehniskā apskate', '技术检验', 'ตรวจสภาพรถ'),
};

export function translateOperation(code: OperationCode, raw: string | null | undefined, locale: string): string {
  const row = OPERATIONS[code];
  return row ? vehicleLabel(row, locale) : raw?.trim() || code;
}

const INSPECTION_TYPES: Record<string, Row> = {
  regular: r('Korraline', 'Regular', 'Плановый', 'Kārtējā', '定期检验', 'ตามกำหนด'),
  repeat: r('Korduv', 'Repeat', 'Повторный', 'Atkārtotā', '复检', 'ตรวจซ้ำ'),
  extraordinary: r('Erakorraline', 'Extraordinary', 'Внеочередной', 'Ārkārtas', '临时检验', 'กรณีพิเศษ'),
  initial: r('Esmane', 'Initial', 'Первичный', 'Sākotnējā', '初次检验', 'ครั้งแรก'),
  other: r('Muu', 'Other', 'Другой', 'Cita', '其他', 'อื่น ๆ'),
};

export function translateInspectionType(
  code: string | null | undefined,
  raw: string | null | undefined,
  locale: string,
): string | null {
  const row = code ? INSPECTION_TYPES[code] : undefined;
  return row ? vehicleLabel(row, locale) : translateGenericValue(raw, locale);
}

const INSPECTION_DECISIONS: Partial<Record<IconStatus, Row>> = {
  ok: r('Korras', 'Passed', 'Пройден', 'Izieta', '通过', 'ผ่าน'),
  major_fault: r('Oluline viga', 'Major fault', 'Серьёзная неисправность', 'Būtisks trūkums', '重大故障', 'ข้อบกพร่องร้ายแรง'),
  minor_fault: r('Väike viga', 'Minor fault', 'Незначительная неисправность', 'Neliels trūkums', '轻微故障', 'ข้อบกพร่องเล็กน้อย'),
  info: r('Info', 'Information', 'Информация', 'Informācija', '信息', 'ข้อมูล'),
  unknown: r('Teadmata', 'Unknown', 'Неизвестно', 'Nav zināms', '未知', 'ไม่ทราบ'),
};

export function translateInspectionDecision(status: IconStatus, locale: string): string {
  const row = INSPECTION_DECISIONS[status];
  return row ? vehicleLabel(row, locale) : status;
}

const INSURANCE_CONTRACTS: Record<string, Row> = {
  liikluskindlustus: r('Liikluskindlustus', 'Motor third-party liability insurance', 'Страхование автогражданской ответственности', 'OCTA apdrošināšana', '机动车第三者责任保险', 'ประกันภัยความรับผิดต่อบุคคลภายนอก'),
};

export function translateInsuranceContract(raw: string | null | undefined, locale: string): string | null {
  return tableValue(INSURANCE_CONTRACTS, raw, locale);
}

export function translateAxleName(raw: string | null | undefined, locale: string): string | null {
  const trimmed = raw?.trim();
  if (!trimmed) return null;
  const match = /^(\d+)\.?\s*telg$/i.exec(trimmed);
  if (!match) return trimmed;
  const number = match[1];
  const templates: Record<string, string> = {
    et: `${number}. telg`,
    en: `Axle ${number}`,
    ru: `Ось ${number}`,
    lv: `${number}. ass`,
    zh: `第${number}轴`,
    th: `เพลาที่ ${number}`,
  };
  return templates[locale] ?? templates.en ?? trimmed;
}
